/**
 * ChatOverlayContext — Feb 2026
 * ==============================
 * App-wide provider that lets any card open the classroom-groups chat as
 * an overlay above the current page (instead of a full Router navigation).
 *
 * Why: navigating with react-router unmounts the whole Dashboard tree
 * (notifications card, quick actions, live monitor, …) and re-mounts it
 * again when the user closes the chat. On lower-end devices this
 * transition adds ~300-800ms of jank per open/close cycle. Opening as
 * an overlay keeps the Dashboard alive under the chat and makes the
 * transition ~instant, matching WhatsApp/Telegram web.
 *
 * We render the overlay through `createPortal` at the document body so
 * it sits above any z-indexed page chrome. History integration:
 *  • open()  → history.pushState so the hardware/browser Back button
 *              closes the overlay first.
 *  • close() → history.back() to keep the URL in sync, unless the
 *              close was itself triggered by a popstate (avoids
 *              recursive back).
 *
 * Consumers:
 *   const { open, close } = useChatOverlay();
 *   open(group);   // group = { classroom_id, classroom_name, ...meta }
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { DashboardLayoutEmbedContext } from '../components/DashboardLayout';
import { UnifiedLoadingState } from '../components/ui-system';

// Lazy-load the chat surface so the overlay code doesn't add anything
// to the initial dashboard bundle. The chat surface itself is already
// code-split via App.js `React.lazy`.
const ClassroomGroups = React.lazy(() =>
  import('../pages/ClassroomGroups').then((m) => ({ default: m.ClassroomGroups })),
);

const ChatOverlayContext = createContext(null);

export const useChatOverlay = () => {
  const ctx = useContext(ChatOverlayContext);
  if (!ctx) {
    // Fallback stub so components that render before the provider
    // mounts (SSR-style shell) never crash. In practice, App.js wraps
    // the entire router with the provider so this branch is unused.
    return { open: () => {}, close: () => {} };
  }
  return ctx;
};

const HISTORY_MARKER = '__mesp_chat_overlay__';

export const ChatOverlayProvider = ({ children }) => {
  const [initialGroup, setInitialGroup] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  // Feb 2026 (Overlay hardening) — the shell mounts on FIRST open and
  // then stays mounted for the lifetime of the app. Subsequent
  // close/re-open cycles just toggle its visibility via CSS. This
  // preserves the ClassroomGroups React tree (messages, scroll
  // position, poll timers, refs) across quick close/open, eliminating
  // the "reload" feel reported on the Teacher/Student overlay path.
  // Switching to a DIFFERENT room while the shell is alive is handled
  // by ClassroomGroups reacting to a change in `initialGroup` prop.
  const [hasEverOpened, setHasEverOpened] = useState(false);
  // Distinguishes "close called from popstate" (skip history.back) from
  // "close called by user click" (do history.back).
  const [closingFromHistory, setClosingFromHistory] = useState(false);

  const open = useCallback((group) => {
    if (!group?.classroom_id) return;
    setInitialGroup(group);
    setIsOpen(true);
    setHasEverOpened(true);
    // Push a history entry so the OS/browser Back button closes the
    // overlay instead of leaving the dashboard.
    try {
      window.history.pushState(
        { [HISTORY_MARKER]: true, cid: group.classroom_id },
        '',
        window.location.pathname + window.location.search + '#chat',
      );
    } catch { /* history API blocked — non-fatal */ }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    // Feb 2026 (Overlay hardening) — DO NOT clear `initialGroup` on
    // close. Keeping it around means the shell stays hydrated with
    // the last-viewed room, so re-opening the same room is
    // instantaneous (no cache-hydrate flicker, no fresh fetch flash).
    // The next `open()` will either bring back the same room (fast
    // path) or supply a new one (in-place room switch inside the
    // still-mounted ClassroomGroups).
    if (!closingFromHistory) {
      try {
        // Only unwind history when the user triggered close via a
        // click. If popstate is what closed us, history is already at
        // the right position.
        if (window.history.state && window.history.state[HISTORY_MARKER]) {
          window.history.back();
        }
      } catch { /* ignore */ }
    }
    setClosingFromHistory(false);
  }, [closingFromHistory]);

  // Wire popstate → close overlay if it's our marker leaving.
  useEffect(() => {
    const onPop = (ev) => {
      // If the state we're popping AWAY from was our overlay marker,
      // and the new state no longer has the marker, close.
      // Jun 2026 — hierarchical back fix: intermediate layers inside the
      // overlay (tabs / assignment detail / lightbox) push their own
      // `{__mesp_bn:1}` entries via useBackNavigation. Popping INTO one
      // of those means we are still inside the overlay — do NOT close.
      const st = ev.state;
      const stillInsideOverlay = st && (st[HISTORY_MARKER] || st.__mesp_bn);
      if (isOpen && !stillInsideOverlay) {
        setClosingFromHistory(true);
        setIsOpen(false);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isOpen]);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <ChatOverlayContext.Provider value={value}>
      {children}
      {hasEverOpened && createPortal(
        <ChatOverlayShell
          initialGroup={initialGroup}
          onClose={close}
          isVisible={isOpen}
        />,
        document.body,
      )}
    </ChatOverlayContext.Provider>
  );
};

/**
 * ChatOverlayShell — persistent visual overlay. Once mounted, it stays
 * in the tree for the app's lifetime; `isVisible` toggles CSS display
 * so React state inside `<ClassroomGroups>` is preserved across
 * close/re-open cycles.
 */
const ChatOverlayShell = ({ initialGroup, onClose, isVisible }) => {
  // Lock body scroll while the overlay is visible.
  useEffect(() => {
    if (!isVisible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isVisible]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0B1F3A]"
      style={{
        display: isVisible ? 'flex' : 'none',
        flexDirection: 'column',
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
      data-testid="chat-overlay-shell"
      data-back-managed="true"
      role="dialog"
      aria-modal={isVisible ? 'true' : undefined}
      aria-hidden={isVisible ? undefined : 'true'}
    >
      <React.Suspense
        fallback={(
          // Feb 2026 (Wave 11) — unified loading language. The overlay
          // background is dark navy, so we render the loader inline
          // (no card frame) and centered.
          <div className="flex items-center justify-center h-full">
            <UnifiedLoadingState variant="inline" size="md" />
          </div>
        )}
      >
        {/* Feb 2026 (Chat Polish Wave 1) — mark the subtree as
            "embedded" so the inner DashboardLayout skips its own
            sidebar/topbar chrome. This is the primary space-saver on
            mobile: previously the overlay stacked two toolbars on top
            of the chat header, consuming ~40% of the viewport before
            a single message appeared. */}
        <DashboardLayoutEmbedContext.Provider value={true}>
          <ClassroomGroups initialGroup={initialGroup} onClose={onClose} isVisible={isVisible} />
        </DashboardLayoutEmbedContext.Provider>
      </React.Suspense>
    </div>
  );
};

export default ChatOverlayProvider;
